import { ElMessage } from 'element-plus'
import { h } from 'vue'
import { useHTTP } from '@/plugins/http'
import Notification from '@/models/notification/Notification'

// Module-level singleton — shared across all component instances
let lastPollTime: Date | null = null
let pollingInterval: ReturnType<typeof setInterval> | null = null

function playBeep() {
  try {
    const audioCtx = new AudioContext()
    const pulseDuration = 0.4
    const gapDuration   = 0.2
    const repeats       = 3

    for (let i = 0; i < repeats; i++) {
      const startTime = audioCtx.currentTime + i * (pulseDuration + gapDuration)
      const osc  = audioCtx.createOscillator()
      const gain = audioCtx.createGain()
      osc.connect(gain)
      gain.connect(audioCtx.destination)
      osc.type = 'sine'
      osc.frequency.setValueAtTime(880, startTime)
      gain.gain.setValueAtTime(0.3, startTime)
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + pulseDuration)
      osc.start(startTime)
      osc.stop(startTime + pulseDuration)
      if (i === repeats - 1) osc.onended = () => audioCtx.close()
    }
  } catch {
    // AudioContext blocked or unavailable — fail silently
  }
}

export function useReminderPoller() {
  const http = useHTTP()

  const poll = async () => {
    try {
      const data = await http.get<Notification[]>(Notification.ENDPOINT_TODAY)
      const fetched = (data ?? []).map(Notification.from)
      const now = new Date()

      const newDue = fetched.filter(n => {
        if (n.entityType !== 'reminder') return false
        const remindAt = new Date(n.date)
        if (remindAt > now) return false
        // First poll: trigger all currently past-due reminders
        if (lastPollTime === null) return true
        // Subsequent polls: only trigger reminders that became due since last poll
        return remindAt > lastPollTime
      })

      lastPollTime = now

      if (newDue.length > 0) {
        playBeep()
        newDue.forEach(n => {
          const msg = ElMessage({
            message: h('div', { style: 'display:flex;align-items:center;gap:16px;padding:2px 4px;' }, [
              h('span', n.title),
              h('button', {
                style: 'font-size:12px;padding:4px 12px;border-radius:4px;background:#e5e7eb;border:none;cursor:pointer;font-weight:600;white-space:nowrap;color:#374151;',
                onClick: async () => {
                  try {
                    await http.post(`${Notification.ENDPOINT_REMINDERS}/${n.entityID}/done`, {})
                  } catch { /* fail silently */ }
                  msg.close()
                },
              }, 'Done'),
            ]),
            type: 'warning',
            duration: 0,
            showClose: true,
          })
        })
      }
    } catch {
      // fail silently — user may not be logged in
    }
  }

  const start = () => {
    if (pollingInterval !== null) return
    pollingInterval = setInterval(poll, 20_000)
  }

  const stop = () => {
    if (pollingInterval !== null) {
      clearInterval(pollingInterval)
      pollingInterval = null
    }
  }

  return { start, stop }
}
